import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { eventsAPI } from '../services/api';
import { Plus, CreditCard as Edit2, Trash2, Calendar, MapPin, User, Clock } from 'lucide-react';
import { getHijriDate, getGregorianDate } from '../utils/dateUtils';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      toast.error('فشل في تحميل الأحداث');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEdit = async (data) => {
    try {
      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, data);
        toast.success('تم تحديث الحدث بنجاح');
      } else {
        await eventsAPI.create(data);
        toast.success('تم إنشاء الحدث بنجاح');
      }
      
      fetchEvents();
      setIsModalOpen(false);
      setEditingEvent(null);
      reset();
    } catch (error) {
      toast.error(editingEvent ? 'فشل في تحديث الحدث' : 'فشل في إنشاء الحدث');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    reset({
      title: event.title,
      description: event.description,
      startDate: new Date(event.startDate).toISOString().slice(0, 16),
      endDate: new Date(event.endDate).toISOString().slice(0, 16),
      location: event.location,
      speaker: event.speaker,
      maxAttendees: event.maxAttendees,
      requiresRegistration: event.requiresRegistration,
      imageUrl: event.imageUrl,
      additionalInfo: event.additionalInfo,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await eventsAPI.delete(id);
      toast.success('تم حذف الحدث بنجاح');
      fetchEvents();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('فشل في حذف الحدث');
    }
  };

  const openCreateModal = () => {
    setEditingEvent(null);
    reset();
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">إدارة الأحداث</h1>
            <p className="text-text-secondary">إدارة أحداث وفعاليات المسجد</p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 ml-2" />
            حدث جديد
          </Button>
        </div>

        {/* Events List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {events.map((event) => (
            <div key={event.id} className="bg-card rounded-xl overflow-hidden border border-divider">
              {event.imageUrl && (
                <img 
                  src={event.imageUrl} 
                  alt={event.title}
                  className="w-full h-48 object-cover"
                />
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold text-text-primary line-clamp-2">
                    {event.title}
                  </h3>
                  <div className="flex space-x-2 space-x-reverse">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(event)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(event)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                  {event.description}
                </p>

                <div className="space-y-2 text-sm">
                  <div className="flex flex-col space-y-1 text-text-muted">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 ml-2" />
                      <span className="text-xs">هجري:</span>
                      <span className="mr-1">{getHijriDate(event.startDate)}</span>
                    </div>
                    <div className="flex items-center mr-6">
                      <span className="text-xs">ميلادي:</span>
                      <span className="mr-1">{getGregorianDate(event.startDate)}</span>
                    </div>
                  </div>

                  <div className="flex items-center text-text-muted">
                    <Clock className="w-4 h-4 ml-2" />
                    {new Date(event.startDate).toLocaleTimeString('ar-SA', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>

                  {event.location && (
                    <div className="flex items-center text-text-muted">
                      <MapPin className="w-4 h-4 ml-2" />
                      {event.location}
                    </div>
                  )}

                  {event.speaker && (
                    <div className="flex items-center text-text-muted">
                      <User className="w-4 h-4 ml-2" />
                      {event.speaker}
                    </div>
                  )}
                </div>

                {event.requiresRegistration && (
                  <div className="mt-4 px-3 py-1 bg-accent-gold/20 text-accent-gold text-xs rounded-full inline-block">
                    يتطلب تسجيل ({event.currentAttendees}/{event.maxAttendees})
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {events.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-text-muted mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد أحداث</h3>
            <p className="text-text-muted">ابدأ بإنشاء حدث جديد للمسجد</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingEvent(null);
          reset();
        }}
        title={editingEvent ? 'تعديل الحدث' : 'حدث جديد'}
        size="lg"
      >
        <form onSubmit={handleSubmit(handleCreateEdit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                عنوان الحدث *
              </label>
              <input
                {...register('title', { required: 'عنوان الحدث مطلوب' })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: درس الفقه"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                تاريخ البداية *
              </label>
              <input
                type="datetime-local"
                {...register('startDate', { required: 'تاريخ البداية مطلوب' })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                تاريخ النهاية *
              </label>
              <input
                type="datetime-local"
                {...register('endDate', { required: 'تاريخ النهاية مطلوب' })}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                المكان
              </label>
              <input
                {...register('location')}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: القاعة الكبرى"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                اسم المتحدث
              </label>
              <input
                {...register('speaker')}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: الشيخ محمد أحمد"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                وصف الحدث *
              </label>
              <textarea
                {...register('description', { required: 'وصف الحدث مطلوب' })}
                rows="4"
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="اكتب وصفاً مفصلاً عن الحدث..."
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                الحد الأقصى للحضور
              </label>
              <input
                type="number"
                {...register('maxAttendees')}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="100"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('requiresRegistration')}
                className="w-4 h-4 text-accent-gold bg-primary-light border-divider rounded focus:ring-accent-gold"
              />
              <label className="mr-2 text-sm text-text-primary">
                يتطلب تسجيل مسبق
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                رابط الصورة
              </label>
              <input
                {...register('imageUrl')}
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-2">
                معلومات إضافية
              </label>
              <textarea
                {...register('additionalInfo')}
                rows="3"
                className="w-full px-3 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="أي معلومات إضافية..."
              />
            </div>
          </div>

          <div className="flex space-x-3 space-x-reverse justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setEditingEvent(null);
                reset();
              }}
            >
              إلغاء
            </Button>
            <Button type="submit">
              {editingEvent ? 'تحديث' : 'إنشاء'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="تأكيد الحذف"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            هل أنت متأكد من حذف الحدث "{deleteConfirm?.title}"؟
          </p>
          <div className="flex space-x-3 space-x-reverse justify-end">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              إلغاء
            </Button>
            <Button
              variant="danger"
              onClick={() => handleDelete(deleteConfirm.id)}
            >
              حذف
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default Events;